import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { fetchOptionsGexCurrent } from '../../lib/optionsApi';
import { formatGexPrice, getGexRegimePresentation, getGexStatusLabel } from './optionsGexUtils';

const emptyGex = {
  data_status: 'unavailable',
  gamma_regime: 'INDETERMINATE',
  gamma_flip: null,
  positive_peak: null,
  negative_peak: null,
};

export const OptionsGexCard = () => {
  const [gex, setGex] = useState(null);

  useEffect(() => {
    let active = true;
    fetchOptionsGexCurrent()
      .then((payload) => active && setGex(payload))
      .catch(() => active && setGex(emptyGex));
    return () => { active = false; };
  }, []);

  const data = gex || emptyGex;
  const regime = getGexRegimePresentation(data.gamma_regime);
  const updatedTime = data.updated_at
    ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(data.updated_at))
    : null;

  return (
    <TooltipProvider>
      <Card className="overflow-hidden border-white/10 bg-[linear-gradient(135deg,rgba(52,211,153,0.08),rgba(255,255,255,0.025)_45%,rgba(248,113,113,0.07))] text-white shadow-none" data-testid="options-gex-card">
        <CardContent className="space-y-4 px-4 py-4 sm:px-5 sm:py-5" data-testid="options-gex-content">
          <div className="flex flex-wrap items-start justify-between gap-3" data-testid="options-gex-header">
            <div data-testid="options-gex-title-group">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-white" data-testid="options-gex-title">GEX — Gamma Exposure</p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-white/40 transition-colors hover:text-white/75" aria-label="Limitação metodológica do GEX" data-testid="options-gex-methodology-tooltip-trigger">
                      <Info size={15} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[280px] border border-white/10 bg-[#11161E] text-white/90" data-testid="options-gex-methodology-tooltip-content">
                    Estimativa agregada pela convenção Call positivo e Put negativo. Não representa o posicionamento líquido real dos dealers.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="mt-1 text-xs text-white/55" data-testid="options-gex-methodology-label">GEX estimado (proxy Call–Put)</p>
            </div>
            <p className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${regime.badge}`} data-testid="options-gex-regime-status">
              {getGexStatusLabel(data.data_status)}
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="options-gex-metrics">
            <GexMetric label="Gamma Regime" value={regime.label} tone={regime.tone} testId="options-gex-gamma-regime" />
            <GexMetric label="Gamma Flip" value={formatGexPrice(data.gamma_flip)} testId="options-gex-gamma-flip" />
            <GexMetric label="Positive Peak (P1)" value={formatGexPrice(data.positive_peak)} tone="text-[#4ADE80]" testId="options-gex-positive-peak" />
            <GexMetric label="Negative Peak (N1)" value={formatGexPrice(data.negative_peak)} tone="text-[#F87171]" testId="options-gex-negative-peak" />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3 text-[10px] text-white/42" data-testid="options-gex-footer">
            <span data-testid="options-gex-source">{data.source || 'Deribit Mainnet · BTC Options'}</span>
            <span data-testid="options-gex-updated-at">{updatedTime ? `Atualizado ${updatedTime}` : 'Aguardando dados de mercado'}</span>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

const GexMetric = ({ label, value, tone = 'text-white', testId }) => (
  <div className="min-w-0 border border-white/10 bg-black/15 px-3 py-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.14em] text-white/42" data-testid={`${testId}-label`}>{label}</p>
    <p className={`mt-1 truncate text-base font-semibold tabular-nums ${tone}`} data-testid={`${testId}-value`}>{value}</p>
  </div>
);

export default OptionsGexCard;